import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/SearchableSelect';
import { PackagePlus, Plus, X, Send, Store } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const ITEM_SEARCH_KEYS = ['name', 'sku', 'code', 'item_code', 'barcode', 'description'];

// On the POS (PIN session) only `cashier_token` exists — the shared api
// interceptor won't attach it to non-/cashier paths like /branches & /items,
// so send it explicitly here to load the branch name + inventory list.
const authHeaders = () => {
  const t = localStorage.getItem('token') || localStorage.getItem('cashier_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * "Request Stock from Branch" entry point (POS Stock menu + Portal). The
 * current branch is the REQUESTER (destination); the picked branch is the
 * source that must fulfil. Items reuse the high-speed Name/Code/SKU search.
 */
export function StockRequestButton({ myBranchId, myBranchName = '', className = '', variant = 'outline', size = 'sm', label = 'Request Stock' }) {
  const [open, setOpen] = useState(false);
  const [branches, setBranches] = useState([]);
  const [currentBranch, setCurrentBranch] = useState(null);
  const [items, setItems] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [fromBranch, setFromBranch] = useState('');
  const [reason, setReason] = useState('');
  const [rows, setRows] = useState([{ item_id: '', quantity: '', kind: 'item' }]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [bR, iR] = await Promise.all([
          api.get('/stock-transfers/source-branches', { params: { for_branch_id: myBranchId || undefined }, headers: authHeaders() }),
          api.get('/items', { headers: authHeaders() }),
        ]);
        setBranches(bR.data?.branches || []);
        setCurrentBranch(bR.data?.current_branch || null);
        setItems(iR.data || []);
        api.get('/cashier/menu-all', { headers: authHeaders() }).then(r => setMenuItems(r.data || [])).catch(() => {});
      } catch { /* silent */ }
    })();
  }, [open]);

  const branchLabel = currentBranch?.name || myBranchName || '';

  const submit = async () => {
    const valid = rows.filter((r) => r.item_id && parseFloat(r.quantity) > 0);
    if (!fromBranch) { toast.error('Select the branch to request from'); return; }
    if (!myBranchId) { toast.error('Your branch is not set'); return; }
    if (fromBranch === myBranchId) { toast.error('Source and your branch must differ'); return; }
    if (!valid.length) { toast.error('Add at least one item'); return; }
    setBusy(true);
    try {
      await api.post('/stock-transfers', {
        from_branch_id: fromBranch,       // source that fulfils
        to_branch_id: myBranchId,         // requester (me)
        reason,
        items: valid.map((r) => ({ item_id: r.item_id, quantity: parseFloat(r.quantity), kind: r.kind || 'item' })),
      }, { headers: authHeaders() });
      toast.success('Stock request sent to branch');
      setOpen(false);
      setFromBranch(''); setReason(''); setRows([{ item_id: '', quantity: '', kind: 'item' }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send request');
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant={variant} size={size} className={className} onClick={() => setOpen(true)} data-testid="request-stock-btn">
        <PackagePlus size={14} className="mr-1" /> {label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90dvh] overflow-y-auto" data-testid="request-stock-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit">Request Stock from Branch</DialogTitle>
            {branchLabel && (
              <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="stock-current-branch">
                <Store size={12} /> Current branch: <span className="font-semibold text-foreground">{branchLabel}</span>
              </p>
            )}
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Request From (source branch)</Label>
              <Select value={fromBranch} onValueChange={setFromBranch}>
                <SelectTrigger className="h-9" data-testid="request-from-branch"><SelectValue placeholder="Select branch…" /></SelectTrigger>
                <SelectContent>
                  {branches.filter((b) => b.id !== myBranchId).map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs mb-1 block">Items Needed</Label>
              <div className="space-y-2">
                {rows.map((row, i) => (
                  <div key={i} className="flex gap-2 items-end">
                    <Select value={row.kind || 'item'} onValueChange={(v) => { const u = [...rows]; u[i].kind = v; u[i].item_id = ''; setRows(u); }}>
                      <SelectTrigger className="w-24 h-9 text-xs shrink-0" data-testid={`request-item-kind-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="item">Inventory</SelectItem>
                        <SelectItem value="menu_item">Menu</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex-1">
                      <SearchableSelect
                        items={(row.kind === 'menu_item') ? menuItems : items}
                        value={row.item_id}
                        onChange={(v) => { const u = [...rows]; u[i].item_id = v; setRows(u); }}
                        searchKeys={ITEM_SEARCH_KEYS}
                        placeholder={row.kind === 'menu_item' ? 'Search menu item…' : 'Search name / code / SKU…'}
                        renderItem={(it) => (
                          <div className="flex items-center justify-between w-full gap-2">
                            <span className="truncate">{it.name}</span>
                            <span className="text-[10px] text-muted-foreground font-mono shrink-0">
                              {row.kind === 'menu_item' ? `SAR ${it.price}` : ((it.sku || it.code || it.item_code || it.barcode) || (it.unit || 'pc'))}
                            </span>
                          </div>
                        )}
                        data-testid={`request-item-select-${i}`}
                      />
                    </div>
                    <Input type="number" placeholder="Qty" value={row.quantity}
                      onChange={(e) => { const u = [...rows]; u[i].quantity = e.target.value; setRows(u); }}
                      className="w-16 h-9 text-xs" data-testid={`request-item-qty-${i}`} />
                    {rows.length > 1 && (
                      <Button size="sm" variant="ghost" className="h-9 px-2" onClick={() => setRows(rows.filter((_, idx) => idx !== i))}>
                        <X size={14} />
                      </Button>
                    )}
                  </div>
                ))}
                <Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={() => setRows([...rows, { item_id: '', quantity: '', kind: 'item' }])} data-testid="request-add-item">
                  <Plus size={12} className="mr-1" /> Add Item
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-xs">Reason</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} className="h-9" data-testid="request-reason" />
            </div>

            <Button className="w-full rounded-xl bg-orange-500 hover:bg-orange-600" onClick={submit} disabled={busy} data-testid="request-submit-btn">
              <Send size={14} className="mr-1" /> {busy ? 'Sending…' : 'Send Request'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default StockRequestButton;
